import { useMemo } from 'react'
import { ToastTypes, launchToast } from '../../../components'
import { ApiError, useApi, useApiMutation } from '../../../utils'
import {
    GetSmsStatusQuerySchema,
    GetSmsStatusVariablesSchema,
    SendSmsCommandMutationSchema,
    SendSmsCommandVariablesSchema,
    type GetSmsStatusVariables,
    type SendSmsCommandVariables
} from '../device_schemas'

export const useGetSmsStatusQuery = (deviceUuid: string | undefined) => {
    const variables = useMemo<GetSmsStatusVariables | undefined>(
        () => (deviceUuid ? GetSmsStatusVariablesSchema.parse({ device_uuid: deviceUuid }) : undefined),
        [deviceUuid]
    )

    const result = useApi('/platform/device/get_sms_status', variables, GetSmsStatusQuerySchema, {
        queryKey: ['platform/device/get_sms_status', variables],
        enabled: !!variables,
        refetchOnWindowFocus: false
    })

    if (result.error) {
        console.error(result.error)
        launchToast(ToastTypes.Error, 'Error: Could not get responses!')
    }

    return {
        data: result.data,
        loading: result.isFetching,
        error: result.error,
        refetch: result.refetch
    }
}

export const useSendSmsCommandMutation = () => {
    const { mutateAsync, isPending, error } = useApiMutation<
        SendSmsCommandVariables,
        typeof SendSmsCommandMutationSchema
    >('/platform/device/send_sms_command', SendSmsCommandMutationSchema, {
        onSuccess: () => {
            launchToast(ToastTypes.Success, 'Successfully sent command!')
        }
    })

    const sendSmsCommand = async (input: SendSmsCommandVariables) => {
        try {
            const variables = SendSmsCommandVariablesSchema.parse(input)
            await mutateAsync(variables)
        } catch (err) {
            const isMissingIccid = err instanceof ApiError && err?.message?.includes('Missing ICCID data')
            if (isMissingIccid) {
                console.warn(err)
            }
            launchToast(
                ToastTypes.Error,
                isMissingIccid ? 'This device is missing ICCID data on our system.' : 'Error: Could not send command!'
            )
        }
    }

    return { sendSmsCommand, loading: isPending, error }
}
